import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBusinessPlan } from "../App";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import ExportButton from "./ui/ExportButton";
import ChartWithExport from "./ui/ChartWithExport";
import TrialBanner from "./subscription/TrialBanner";
import TooltipLabel from "./ui/TooltipLabel";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Users, 
  Package, 
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  BarChart3,
  Activity,
  Zap,
  TrendingDown as BurnIcon,
  Clock,
  Shield,
  Percent,
  Wallet,
  AlertTriangle,
  CheckCircle2,
  MinusCircle,
  TrendingUp as FavorableIcon,
  TrendingDown as UnfavorableIcon
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, ComposedChart, AreaChart, Area, Legend } from "recharts";
import { usePlanningYears, formatPlanningDateRange } from "../hooks/usePlanningYears";
import { outputTooltips } from "../utils/outputSectionsTooltips";
import { formatCurrency as formatCurrencyUtil, formatPercentage as formatPercentageUtil } from "../utils/sharedCalculations";
import { useCurrency } from "../contexts/CurrencyContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { t } = useTranslation(['dashboard', 'common', 'financial']);
  const { currentPlan, activeScenario } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [financialStatements, setFinancialStatements] = useState(null);
  const [kpis, setKPIs] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // TASK 2: Variance Analysis state
  const [varianceData, setVarianceData] = useState(null);
  const [varianceLoading, setVarianceLoading] = useState(false);
  
  // Use dynamic planning years hook
  const planningData = usePlanningYears(currentPlan?.id);
  const { years, planningSeasonStart, planningSeasonEnd, loading: yearsLoading } = planningData;

  useEffect(() => {
    if (currentPlan) {
      fetchDashboardData();
      fetchVarianceAnalysis();
    }
  }, [currentPlan, activeScenario]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Fetch data for multiple years
      const yearlyData = {};
      for (const year of years) {
        const response = await axios.get(`${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${year}`);
        yearlyData[year] = response.data.financial_statements;
      }
      setFinancialStatements(yearlyData);
      calculateKPIs(yearlyData);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // TASK 2: Fetch Variance Analysis data
  const fetchVarianceAnalysis = async () => {
    try {
      setVarianceLoading(true);
      const currentYear = new Date().getFullYear();
      console.log('Fetching variance analysis for:', currentPlan.id, currentYear);
      const response = await axios.get(`${BACKEND_URL}/api/performance/plan-vs-actual`, {
        params: {
          plan_id: currentPlan.id,
          year: currentYear
        }
      });
      
      console.log('Variance API response:', response.data);
      if (response.data) {
        calculateVarianceMetrics(response.data);
      }
    } catch (error) {
      console.error("Error fetching variance analysis:", error);
      // Set empty variance data on error
      setVarianceData(null);
    } finally {
      setVarianceLoading(false);
    }
  };

  // TASK 2: Calculate variance metrics from plan vs actual data
  const calculateVarianceMetrics = (data) => {
    console.log('Calculating variance metrics from:', data);
    const currentYear = new Date().getFullYear();
    const planData = financialStatements?.[currentYear]?.income_statement || {};
    
    // Extract actual data
    const actualRevenue = data.enhanced_summary?.total_actual_revenue || 0;
    const actualExpenses = data.enhanced_summary?.total_actual_expenses || 0;
    const actualProfit = actualRevenue - actualExpenses;
    
    console.log('Variance calculation:', { actualRevenue, actualExpenses, actualProfit, planData });
    
    // Extract plan data
    const planRevenue = planData.revenue || 0;
    const planExpenses = planData.operating_expenses || 0;
    const planProfit = planData.net_income || 0;
    
    // Calculate variances
    const revenueVariance = actualRevenue - planRevenue;
    const revenueVariancePct = planRevenue > 0 ? (revenueVariance / planRevenue) * 100 : 0;
    const revenueFavorable = revenueVariance >= 0;
    
    const expenseVariance = actualExpenses - planExpenses;
    const expenseVariancePct = planExpenses > 0 ? (expenseVariance / planExpenses) * 100 : 0;
    const expenseFavorable = expenseVariance <= 0; // Lower expenses are favorable
    
    const profitVariance = actualProfit - planProfit;
    const profitVariancePct = planProfit > 0 ? (profitVariance / planProfit) * 100 : 0;
    const profitFavorable = profitVariance >= 0;
    
    // YTD calculations (simplified - can be enhanced with actual YTD logic)
    const monthsElapsed = new Date().getMonth() + 1;
    const ytdPlanRevenue = (planRevenue / 12) * monthsElapsed;
    const ytdActualRevenue = actualRevenue; // Assuming actual is YTD
    const ytdVariance = ytdActualRevenue - ytdPlanRevenue;
    const ytdVariancePct = ytdPlanRevenue > 0 ? (ytdVariance / ytdPlanRevenue) * 100 : 0;
    
    setVarianceData({
      revenue: {
        actual: actualRevenue,
        plan: planRevenue,
        variance: revenueVariance,
        variancePct: revenueVariancePct,
        favorable: revenueFavorable
      },
      expenses: {
        actual: actualExpenses,
        plan: planExpenses,
        variance: expenseVariance,
        variancePct: expenseVariancePct,
        favorable: expenseFavorable
      },
      profit: {
        actual: actualProfit,
        plan: planProfit,
        variance: profitVariance,
        variancePct: profitVariancePct,
        favorable: profitFavorable
      },
      ytd: {
        actual: ytdActualRevenue,
        plan: ytdPlanRevenue,
        variance: ytdVariance,
        variancePct: ytdVariancePct
      },
      year: currentYear
    });
  };

  const calculateKPIs = (statements) => {
    const statementsYears = Object.keys(statements).sort();
    const currentYear = statementsYears[statementsYears.length - 1];
    const previousYear = statementsYears[statementsYears.length - 2];
    
    if (!statements[currentYear] || !statements[previousYear]) return;

    const current = statements[currentYear]?.income_statement || {};
    const previous = statements[previousYear]?.income_statement || {};
    const currentBS = statements[currentYear]?.balance_sheet || {};
    const currentCF = statements[currentYear]?.cash_flow || {};

    // Defensive checks for all required fields - Income Statement
    const currentRevenue = current.revenue || 0;
    const previousRevenue = previous.revenue || 0;
    const currentGrossProfit = current.gross_profit || 0;
    const previousGrossProfit = previous.gross_profit || 0;
    const currentNetIncome = current.net_income || 0;
    const previousNetIncome = previous.net_income || 0;
    const currentEbit = current.ebit || 0;
    const currentDepreciation = current.depreciation || 0;

    // Balance Sheet items for new KPIs
    const currentAssets = currentBS.current_assets || 0;
    const currentLiabilities = currentBS.current_liabilities || 0;
    const inventory = currentBS.inventory || 0;
    const totalDebt = currentBS.total_debt || 0;
    const totalEquity = currentBS.total_equity || 0;
    const cash = currentBS.cash || 0;

    // Cash Flow items
    const operatingCF = currentCF.operating_cash_flow || 0;

    // Existing KPIs
    const revenueGrowth = previousRevenue > 0 ? ((currentRevenue - previousRevenue) / previousRevenue) * 100 : 0;
    const grossMargin = currentRevenue > 0 ? (currentGrossProfit / currentRevenue) * 100 : 0;
    const previousGrossMargin = previousRevenue > 0 ? (previousGrossProfit / previousRevenue) * 100 : 0;
    const grossMarginChange = grossMargin - previousGrossMargin;
    const netMargin = currentRevenue > 0 ? (currentNetIncome / currentRevenue) * 100 : 0;
    const ebitdaMargin = currentRevenue > 0 ? ((currentEbit + currentDepreciation) / currentRevenue) * 100 : 0;
    const netIncomeGrowth = previousNetIncome > 0 ? ((currentNetIncome - previousNetIncome) / previousNetIncome) * 100 : 0;

    // TASK 3: Multi-year trend data for sparklines
    const revenueTrend = statementsYears.map(year => ({
      year: year,
      value: (statements[year]?.income_statement?.revenue || 0) / 1000000
    }));

    const netIncomeTrend = statementsYears.map(year => ({
      year: year,
      value: (statements[year]?.income_statement?.net_income || 0) / 1000000
    }));

    const grossMarginTrend = statementsYears.map(year => {
      const revenue = statements[year]?.income_statement?.revenue || 0;
      const grossProfit = statements[year]?.income_statement?.gross_profit || 0;
      return {
        year: year,
        value: revenue > 0 ? (grossProfit / revenue) * 100 : 0
      };
    });

    // TASK 3: Status indicators for existing KPIs based on performance
    const revenueGrowthStatus = revenueGrowth >= 20 ? 'excellent' : revenueGrowth >= 10 ? 'good' : revenueGrowth >= 0 ? 'caution' : 'critical';
    const netIncomeStatus = currentNetIncome > 0 ? (netIncomeGrowth >= 15 ? 'excellent' : netIncomeGrowth >= 5 ? 'good' : netIncomeGrowth >= 0 ? 'caution' : 'critical') : 'critical';
    const grossMarginStatus = grossMargin >= 40 ? 'excellent' : grossMargin >= 30 ? 'good' : grossMargin >= 20 ? 'caution' : 'critical';

    // NEW PHASE 1 KPIs - Liquidity Ratios
    const currentRatio = currentLiabilities > 0 ? currentAssets / currentLiabilities : 0;
    const quickRatio = currentLiabilities > 0 ? (currentAssets - inventory) / currentLiabilities : 0;
    
    // NEW PHASE 1 KPIs - Leverage Ratios
    const debtToEquity = totalEquity > 0 ? totalDebt / totalEquity : 0;
    
    // NEW PHASE 1 KPIs - Working Capital
    const workingCapital = currentAssets - currentLiabilities;
    
    // NEW PHASE 1 KPIs - Cash Flow Ratios
    const operatingCFRatio = currentLiabilities > 0 ? operatingCF / currentLiabilities : 0;
    
    // NEW PHASE 1 KPIs - Burn Rate & Runway
    // Calculate monthly burn rate from annual operating expenses
    const totalOperatingExpenses = (current.operating_expenses || 0) + (current.depreciation || 0);
    const monthlyBurnRate = totalOperatingExpenses / 12;
    const cashRunway = monthlyBurnRate > 0 ? cash / monthlyBurnRate : 0;

    // Status indicators based on universally accepted thresholds
    const currentRatioStatus = currentRatio >= 2.0 ? 'excellent' : currentRatio >= 1.5 ? 'good' : currentRatio >= 1.0 ? 'caution' : 'critical';
    const quickRatioStatus = quickRatio >= 1.0 ? 'excellent' : quickRatio >= 0.75 ? 'good' : quickRatio >= 0.5 ? 'caution' : 'critical';
    const debtToEquityStatus = debtToEquity <= 1.0 ? 'excellent' : debtToEquity <= 2.0 ? 'good' : debtToEquity <= 3.0 ? 'caution' : 'critical';
    const cashRunwayStatus = cashRunway >= 12 ? 'excellent' : cashRunway >= 6 ? 'good' : cashRunway >= 3 ? 'caution' : 'critical';

    setKPIs({
      // Existing KPIs
      revenue: currentRevenue,
      revenueGrowth,
      revenueGrowthStatus,
      grossMargin,
      grossMarginChange,
      grossMarginStatus,
      netMargin,
      ebitdaMargin,
      netIncome: currentNetIncome,
      netIncomeGrowth,
      netIncomeStatus,
      currentYear,
      previousYear,
      
      // TASK 3: Trend data for sparklines
      revenueTrend,
      netIncomeTrend,
      grossMarginTrend,
      
      // NEW PHASE 1 KPIs
      currentRatio,
      currentRatioStatus,
      quickRatio,
      quickRatioStatus,
      debtToEquity,
      debtToEquityStatus,
      workingCapital,
      operatingCFRatio,
      monthlyBurnRate,
      cashRunway,
      cashRunwayStatus,
      cash
    });
  };

  const formatCurrency = (amount, currency = null) => {
    // Use currency context for automatic conversion
    // If currency is provided, it's the source currency; otherwise use plan's currency
    const sourceCurrency = currency || currentPlan?.currency || "USD";
    return formatCurrencyContext(amount, sourceCurrency, null, false);
  };

  const formatPercentage = (value) => {
    return formatPercentageUtil(value, 1);
  };

  // Helper function to get status badge styling
  const getStatusBadge = (status) => {
    const badges = {
      excellent: { bg: 'bg-green-100', text: 'text-green-700', label: '🟢 Excellent' },
      good: { bg: 'bg-blue-100', text: 'text-blue-700', label: '🔵 Good' },
      caution: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: '🟡 Caution' },
      critical: { bg: 'bg-red-100', text: 'text-red-700', label: '🔴 Critical' }
    };
    return badges[status] || badges.good;
  };

  // TASK 3: Mini Sparkline Component for trend visualization
  const MiniSparkline = ({ data, color = "#3b82f6", height = 40 }) => {
    if (!data || data.length === 0) return null;
    
    return (
      <div style={{ width: '100%', height: height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={color} 
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Prepare chart data
  const chartData = (() => {
    if (!financialStatements) return [];
    
    return Object.keys(financialStatements).sort().map(year => {
      const is = financialStatements[year]?.income_statement || {};
      const cf = financialStatements[year]?.cash_flow || {};
      
      // Add defensive checks for required fields
      const revenue = is.revenue || 0;
      const netIncome = is.net_income || 0;
      const grossProfit = is.gross_profit || 0;
      const ebit = is.ebit || 0;
      const depreciation = is.depreciation || 0;
      
      return {
        year: year,
        revenue: revenue / 1000000,
        netIncome: netIncome / 1000000,
        grossMargin: revenue > 0 ? (grossProfit / revenue) * 100 : 0,
        netMargin: revenue > 0 ? (netIncome / revenue) * 100 : 0,
        ebitdaMargin: revenue > 0 ? ((ebit + depreciation) / revenue) * 100 : 0,
        operatingCF: (cf.operating_cash_flow || 0) / 1000000,
        investingCF: (cf.investing_cash_flow || 0) / 1000000,
        financingCF: (cf.financing_cash_flow || 0) / 1000000
      };
    });
  })();

  const getRevenueChartData = () => {
    return chartData;
  };

  const getMarginChartData = () => {
    return chartData;
  };

  const getCashFlowChartData = () => {
    return chartData;
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/2 md:w-1/4"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-200 rounded"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-80 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6 md:space-y-8 fade-in pt-16 lg:pt-0" id="dashboard-content">
      {/* Trial Banner */}
      <TrialBanner />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <TooltipLabel
            label={<h1 className="text-3xl font-bold text-slate-900 mb-2">{t('dashboard:title')}</h1>}
            tooltip={outputTooltips.dashboard.executive_dashboard}
            asChild
          />
          <p className="text-slate-600">
            {t('dashboard:overview')} {currentPlan?.company_name} - {activeScenario} {t('common:labels.scenario')} | {t('dashboard:widgets.strategicPlanning')}
          </p>
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs md:text-sm">
            <p className="text-yellow-800">
              <strong>{t('dashboard:title')}:</strong> {t('dashboard:widgets.performanceNote')}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <ExportButton
            targetId="dashboard-content"
            componentName="Executive_Dashboard"
            formats={['png']}
            size="sm"
            variant="outline"
            showIcon={true}
            showText={false}
            className="bg-white hover:bg-gray-50 md:inline-flex"
          />
          <div className="bg-white rounded-lg px-4 py-2 shadow-soft border">
            <div className="flex items-center text-sm">
              <Calendar className="h-4 w-4 mr-2 text-slate-500" />
              <span className="text-slate-600">
                {planningSeasonStart && planningSeasonEnd 
                  ? formatPlanningDateRange(planningSeasonStart, planningSeasonEnd, 'short')
                  : '2025-2029'}
              </span>
            </div>
          </div>
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg px-3 py-2 shadow-soft text-xs md:text-sm">
            <div className="flex items-center font-medium">
              <BarChart3 className="h-3 w-3 md:h-4 md:w-4 mr-2" />
              <span>{activeScenario}</span>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {kpis && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {/* TASK 3 ENHANCED: Revenue Card with Sparkline & Status */}
          <Card className="glass-card shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1">
                  <TooltipLabel
                    label={<p className="text-slate-600 text-xs font-medium uppercase tracking-wide">{t('dashboard:metrics.revenue')} ({kpis.currentYear})</p>}
                    tooltip={outputTooltips.dashboard.revenue}
                  />
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {formatCurrency(kpis.revenue, currentPlan.currency)}
                  </p>
                </div>
                <div className="bg-green-100 rounded-full p-3">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
              
              {/* Sparkline */}
              {kpis.revenueTrend && kpis.revenueTrend.length > 0 && (
                <div className="mb-3">
                  <MiniSparkline data={kpis.revenueTrend} color="#10b981" height={30} />
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {kpis.revenueGrowth >= 0 ? (
                    <ArrowUpRight className="h-4 w-4 text-green-600 mr-1" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 text-red-600 mr-1" />
                  )}
                  <TooltipLabel
                    label={
                      <span className={`text-sm font-medium ${kpis.revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercentage(Math.abs(kpis.revenueGrowth))} YoY
                      </span>
                    }
                    tooltip={outputTooltips.dashboard.revenue_growth}
                  />
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(kpis.revenueGrowthStatus).bg} ${getStatusBadge(kpis.revenueGrowthStatus).text} font-medium`}>
                  {getStatusBadge(kpis.revenueGrowthStatus).label.split(' ')[0]}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* TASK 3 ENHANCED: Net Income Card with Sparkline & Status */}
          <Card className="glass-card shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1">
                  <TooltipLabel
                    label={<p className="text-slate-600 text-xs font-medium uppercase tracking-wide">{t('dashboard:metrics.netIncome')} ({kpis.currentYear})</p>}
                    tooltip={outputTooltips.dashboard.net_income}
                  />
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {formatCurrency(kpis.netIncome, currentPlan.currency)}
                  </p>
                </div>
                <div className="bg-blue-100 rounded-full p-3">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              
              {/* Sparkline */}
              {kpis.netIncomeTrend && kpis.netIncomeTrend.length > 0 && (
                <div className="mb-3">
                  <MiniSparkline data={kpis.netIncomeTrend} color="#3b82f6" height={30} />
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {kpis.netIncomeGrowth >= 0 ? (
                    <ArrowUpRight className="h-4 w-4 text-green-600 mr-1" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 text-red-600 mr-1" />
                  )}
                  <TooltipLabel
                    label={
                      <span className={`text-sm font-medium ${kpis.netIncomeGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercentage(Math.abs(kpis.netIncomeGrowth))} YoY
                      </span>
                    }
                    tooltip="Year-over-year net income growth rate"
                  />
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(kpis.netIncomeStatus).bg} ${getStatusBadge(kpis.netIncomeStatus).text} font-medium`}>
                  {getStatusBadge(kpis.netIncomeStatus).label.split(' ')[0]}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* TASK 3 ENHANCED: Gross Margin Card with Sparkline & Status */}
          <Card className="glass-card shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1">
                  <TooltipLabel
                    label={<p className="text-slate-600 text-xs font-medium uppercase tracking-wide">{t('dashboard:metrics.grossMargin')} ({kpis.currentYear})</p>}
                    tooltip={outputTooltips.dashboard.gross_margin}
                  />
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {formatPercentage(kpis.grossMargin)}
                  </p>
                </div>
                <div className="bg-purple-100 rounded-full p-3">
                  <Target className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              
              {/* Sparkline */}
              {kpis.grossMarginTrend && kpis.grossMarginTrend.length > 0 && (
                <div className="mb-3">
                  <MiniSparkline data={kpis.grossMarginTrend} color="#a855f7" height={30} />
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {kpis.grossMarginChange >= 0 ? (
                    <ArrowUpRight className="h-4 w-4 text-green-600 mr-1" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 text-red-600 mr-1" />
                  )}
                  <TooltipLabel
                    label={
                      <span className={`text-sm font-medium ${kpis.grossMarginChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {kpis.grossMarginChange >= 0 ? '+' : ''}{kpis.grossMarginChange.toFixed(1)}% YoY
                      </span>
                    }
                    tooltip="Year-over-year gross margin change"
                  />
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(kpis.grossMarginStatus).bg} ${getStatusBadge(kpis.grossMarginStatus).text} font-medium`}>
                  {getStatusBadge(kpis.grossMarginStatus).label.split(' ')[0]}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* TASK 3 ENHANCED: Scenario Card with Better Styling */}
          <Card className="glass-card shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1">
                  <TooltipLabel
                    label={<p className="text-slate-600 text-xs font-medium uppercase tracking-wide">{t('dashboard:widgets.activeScenario')}</p>}
                    tooltip={outputTooltips.dashboard.scenario_display}
                  />
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {activeScenario}
                  </p>
                </div>
                <div className="bg-orange-100 rounded-full p-3">
                  <BarChart3 className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              
              <div className="mt-6 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Planning Horizon:</span>
                  <span className="font-medium text-slate-900">{years.length} Years</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Period:</span>
                  <span className="font-medium text-slate-900">{years[0]} - {years[years.length - 1]}</span>
                </div>
                <div className="mt-3">
                  <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700 font-medium">
                    📊 Strategic Plan
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* NEW PHASE 1 FINANCIAL KPIs Section */}
      {kpis && (
        <>
          <div className="border-t pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-blue-600" />
              Financial Health Metrics
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {/* Current Ratio */}
              <Card className="glass-card shadow-soft border-l-4 border-l-blue-500">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <TooltipLabel
                        label={<p className="text-slate-600 text-xs font-medium uppercase tracking-wide">{t('dashboard:metrics.currentRatio')}</p>}
                        tooltip="Measures ability to pay short-term obligations. Ratio of current assets to current liabilities. Threshold: >2.0 Excellent, 1.5-2.0 Good, 1.0-1.5 Caution, <1.0 Critical"
                      />
                    </div>
                    <div className="bg-blue-100 rounded-full p-2">
                      <Activity className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-slate-900 mb-2">
                    {kpis.currentRatio?.toFixed(2) || '0.00'}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(kpis.currentRatioStatus).bg} ${getStatusBadge(kpis.currentRatioStatus).text} font-medium`}>
                      {getStatusBadge(kpis.currentRatioStatus).label}
                    </span>
                    <span className="text-xs text-slate-500">Liquidity</span>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Ratio */}
              <Card className="glass-card shadow-soft border-l-4 border-l-cyan-500">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <TooltipLabel
                        label={<p className="text-slate-600 text-xs font-medium uppercase tracking-wide">{t('dashboard:metrics.quickRatio')}</p>}
                        tooltip="Acid-test ratio measuring immediate liquidity. (Current Assets - Inventory) / Current Liabilities. Threshold: >1.0 Excellent, 0.75-1.0 Good, 0.5-0.75 Caution, <0.5 Critical"
                      />
                    </div>
                    <div className="bg-cyan-100 rounded-full p-2">
                      <Zap className="h-4 w-4 text-cyan-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-slate-900 mb-2">
                    {kpis.quickRatio?.toFixed(2) || '0.00'}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(kpis.quickRatioStatus).bg} ${getStatusBadge(kpis.quickRatioStatus).text} font-medium`}>
                      {getStatusBadge(kpis.quickRatioStatus).label}
                    </span>
                    <span className="text-xs text-slate-500">Acid Test</span>
                  </div>
                </CardContent>
              </Card>

              {/* Debt-to-Equity Ratio */}
              <Card className="glass-card shadow-soft border-l-4 border-l-purple-500">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <TooltipLabel
                        label={<p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Debt-to-Equity</p>}
                        tooltip="Measures financial leverage. Total Debt / Total Equity. Threshold: <1.0 Excellent, 1.0-2.0 Good, 2.0-3.0 Caution, >3.0 Critical"
                      />
                    </div>
                    <div className="bg-purple-100 rounded-full p-2">
                      <Shield className="h-4 w-4 text-purple-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-slate-900 mb-2">
                    {kpis.debtToEquity?.toFixed(2) || '0.00'}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(kpis.debtToEquityStatus).bg} ${getStatusBadge(kpis.debtToEquityStatus).text} font-medium`}>
                      {getStatusBadge(kpis.debtToEquityStatus).label}
                    </span>
                    <span className="text-xs text-slate-500">Leverage</span>
                  </div>
                </CardContent>
              </Card>

              {/* Working Capital */}
              <Card className="glass-card shadow-soft border-l-4 border-l-green-500">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <TooltipLabel
                        label={<p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Working Capital</p>}
                        tooltip="Operating liquidity available. Current Assets - Current Liabilities. Positive is good, higher is better for operational flexibility."
                      />
                    </div>
                    <div className="bg-green-100 rounded-full p-2">
                      <Wallet className="h-4 w-4 text-green-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-slate-900 mb-2">
                    {formatCurrency(kpis.workingCapital || 0, currentPlan.currency)}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded-full ${kpis.workingCapital > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} font-medium`}>
                      {kpis.workingCapital > 0 ? '🟢 Positive' : '🔴 Negative'}
                    </span>
                    <span className="text-xs text-slate-500">Operations</span>
                  </div>
                </CardContent>
              </Card>

              {/* Cash Runway */}
              <Card className="glass-card shadow-soft border-l-4 border-l-orange-500">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <TooltipLabel
                        label={<p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Cash Runway</p>}
                        tooltip="Months until cash runs out at current burn rate. Cash / Monthly Burn Rate. Threshold: >12mo Excellent, 6-12mo Good, 3-6mo Caution, <3mo Critical"
                      />
                    </div>
                    <div className="bg-orange-100 rounded-full p-2">
                      <Clock className="h-4 w-4 text-orange-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-slate-900 mb-2">
                    {kpis.cashRunway?.toFixed(1) || '0.0'}
                    <span className="text-lg text-slate-500 ml-1">mo</span>
                  </p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(kpis.cashRunwayStatus).bg} ${getStatusBadge(kpis.cashRunwayStatus).text} font-medium`}>
                      {getStatusBadge(kpis.cashRunwayStatus).label}
                    </span>
                    <span className="text-xs text-slate-500">Survival</span>
                  </div>
                </CardContent>
              </Card>

              {/* Burn Rate */}
              <Card className="glass-card shadow-soft border-l-4 border-l-red-500">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <TooltipLabel
                        label={<p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Monthly Burn Rate</p>}
                        tooltip="Monthly cash spent on operations. Total Operating Expenses / 12. Lower is better for extending runway."
                      />
                    </div>
                    <div className="bg-red-100 rounded-full p-2">
                      <BurnIcon className="h-4 w-4 text-red-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-slate-900 mb-2">
                    {formatCurrency(kpis.monthlyBurnRate || 0, currentPlan.currency)}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">per month</span>
                    <span className="text-xs font-medium text-slate-700">Cash: {formatCurrency(kpis.cash || 0, currentPlan.currency)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Operating Cash Flow Ratio */}
              <Card className="glass-card shadow-soft border-l-4 border-l-indigo-500">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <TooltipLabel
                        label={<p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Operating CF Ratio</p>}
                        tooltip="Ability to cover current liabilities with operating cash flow. Operating CF / Current Liabilities. >1.0 is excellent."
                      />
                    </div>
                    <div className="bg-indigo-100 rounded-full p-2">
                      <Percent className="h-4 w-4 text-indigo-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-slate-900 mb-2">
                    {kpis.operatingCFRatio?.toFixed(2) || '0.00'}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded-full ${kpis.operatingCFRatio >= 1.0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'} font-medium`}>
                      {kpis.operatingCFRatio >= 1.0 ? '🟢 Strong' : '🟡 Weak'}
                    </span>
                    <span className="text-xs text-slate-500">Cash Coverage</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}

      {/* TASK 2: VARIANCE ANALYSIS SECTION */}
      {varianceData && !varianceLoading && (
        <div className="border-t pt-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
            Variance Analysis - Actual vs Budget ({varianceData.year})
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {/* Revenue Variance */}
            <Card className={`glass-card shadow-soft border-l-4 ${varianceData.revenue.favorable ? 'border-l-green-500' : 'border-l-red-500'}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Revenue Variance</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      {formatCurrency(Math.abs(varianceData.revenue.variance), currentPlan.currency)}
                    </p>
                  </div>
                  {varianceData.revenue.favorable ? (
                    <FavorableIcon className="h-6 w-6 text-green-600" />
                  ) : (
                    <UnfavorableIcon className="h-6 w-6 text-red-600" />
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Actual:</span>
                    <span className="font-medium">{formatCurrency(varianceData.revenue.actual, currentPlan.currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Budget:</span>
                    <span className="font-medium">{formatCurrency(varianceData.revenue.plan, currentPlan.currency)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className={`font-medium ${varianceData.revenue.favorable ? 'text-green-600' : 'text-red-600'}`}>
                      {varianceData.revenue.favorable ? '▲' : '▼'} {Math.abs(varianceData.revenue.variancePct).toFixed(1)}%
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${varianceData.revenue.favorable ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} font-medium`}>
                      {varianceData.revenue.favorable ? 'Favorable' : 'Unfavorable'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Expense Variance */}
            <Card className={`glass-card shadow-soft border-l-4 ${varianceData.expenses.favorable ? 'border-l-green-500' : 'border-l-red-500'}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Expense Variance</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      {formatCurrency(Math.abs(varianceData.expenses.variance), currentPlan.currency)}
                    </p>
                  </div>
                  {varianceData.expenses.favorable ? (
                    <FavorableIcon className="h-6 w-6 text-green-600" />
                  ) : (
                    <UnfavorableIcon className="h-6 w-6 text-red-600" />
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Actual:</span>
                    <span className="font-medium">{formatCurrency(varianceData.expenses.actual, currentPlan.currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Budget:</span>
                    <span className="font-medium">{formatCurrency(varianceData.expenses.plan, currentPlan.currency)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className={`font-medium ${varianceData.expenses.favorable ? 'text-green-600' : 'text-red-600'}`}>
                      {varianceData.expenses.favorable ? '▼' : '▲'} {Math.abs(varianceData.expenses.variancePct).toFixed(1)}%
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${varianceData.expenses.favorable ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} font-medium`}>
                      {varianceData.expenses.favorable ? 'Favorable' : 'Unfavorable'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Profit Variance */}
            <Card className={`glass-card shadow-soft border-l-4 ${varianceData.profit.favorable ? 'border-l-green-500' : 'border-l-red-500'}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Profit Variance</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      {formatCurrency(Math.abs(varianceData.profit.variance), currentPlan.currency)}
                    </p>
                  </div>
                  {varianceData.profit.favorable ? (
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  ) : (
                    <MinusCircle className="h-6 w-6 text-red-600" />
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Actual:</span>
                    <span className="font-medium">{formatCurrency(varianceData.profit.actual, currentPlan.currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Budget:</span>
                    <span className="font-medium">{formatCurrency(varianceData.profit.plan, currentPlan.currency)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className={`font-medium ${varianceData.profit.favorable ? 'text-green-600' : 'text-red-600'}`}>
                      {varianceData.profit.favorable ? '▲' : '▼'} {Math.abs(varianceData.profit.variancePct).toFixed(1)}%
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${varianceData.profit.favorable ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} font-medium`}>
                      {varianceData.profit.favorable ? 'Favorable' : 'Unfavorable'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* YTD Performance */}
            <Card className="glass-card shadow-soft border-l-4 border-l-blue-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">YTD vs Plan</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      {formatCurrency(Math.abs(varianceData.ytd.variance), currentPlan.currency)}
                    </p>
                  </div>
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">YTD Actual:</span>
                    <span className="font-medium">{formatCurrency(varianceData.ytd.actual, currentPlan.currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">YTD Plan:</span>
                    <span className="font-medium">{formatCurrency(varianceData.ytd.plan, currentPlan.currency)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className={`font-medium ${varianceData.ytd.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {varianceData.ytd.variance >= 0 ? '▲' : '▼'} {Math.abs(varianceData.ytd.variancePct).toFixed(1)}%
                    </span>
                    <span className="text-xs text-slate-500">
                      {new Date().getMonth() + 1} months
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Variance Analysis Info Banner */}
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Variance Analysis shows the difference between your planned budget and actual performance.
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  ✅ Favorable variances (green): Revenue higher than plan or Expenses lower than plan.
                  ❌ Unfavorable variances (red): Revenue lower than plan or Expenses higher than plan.
                  Visit <strong>Performance Tracking → Actual Data Entry</strong> to record actual performance data.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State for Variance Analysis */}
      {!varianceData && !varianceLoading && (
        <div className="border-t pt-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
            Variance Analysis - Actual vs Budget
          </h2>
          
          <Card className="glass-card shadow-soft">
            <CardContent className="p-8 text-center">
              <div className="flex flex-col items-center">
                <AlertTriangle className="h-12 w-12 text-slate-400 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No Actual Data Available</h3>
                <p className="text-slate-600 mb-4 max-w-md">
                  Record actual performance data to see variance analysis comparing your budget vs actual results.
                </p>
                <button
                  onClick={() => window.location.href = '/actual-data-entry'}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Go to Actual Data Entry
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Revenue & Net Income Chart */}
        <ChartWithExport
          chartId="revenue-net-income-chart"
          chartName="Revenue_Net_Income_Trend"
          formats={['png']}
          exportPosition="top-right"
        >
          <Card className="glass-card shadow-soft">
            <CardHeader>
              <TooltipLabel
                label={<CardTitle className="text-lg">Revenue & Net Income Trend</CardTitle>}
                tooltip={outputTooltips.dashboard.revenue_net_income_trend}
                asChild
              />
              <CardDescription>{years.length}-year financial performance (in millions)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 md:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="year" stroke="#64748b" />
                    <YAxis stroke="#64748b" />
                    <Tooltip formatter={(value) => formatCurrencyContext(value * 1000000)} />
                    <Legend />
                    <Bar dataKey="revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Revenue" />
                    <Line 
                      type="monotone" 
                      dataKey="netIncome" 
                      stroke="#10b981" 
                      strokeWidth={3}
                      name="Net Income"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </ChartWithExport>

        {/* Margin Analysis Chart */}
        <ChartWithExport
          chartId="profitability-margins-chart"
          chartName="Profitability_Margins"
          formats={['png']}
          exportPosition="top-right"
        >
          <Card className="glass-card shadow-soft">
            <CardHeader>
              <TooltipLabel
                label={<CardTitle className="text-lg">Profitability Margins</CardTitle>}
                tooltip={outputTooltips.dashboard.profitability_margins}
                asChild
              />
              <CardDescription>Gross, EBITDA, and Net margins over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 md:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="year" stroke="#64748b" />
                    <YAxis stroke="#64748b" />
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="grossMargin" 
                      stroke="#f59e0b" 
                      strokeWidth={3}
                      name="Gross Margin"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="ebitdaMargin" 
                      stroke="#8b5cf6" 
                      strokeWidth={3}
                      name="EBITDA Margin"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="netMargin" 
                      stroke="#ef4444" 
                      strokeWidth={3}
                      name="Net Margin"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </ChartWithExport>

        {/* Cash Flow Chart */}
        <ChartWithExport
          chartId="cash-flow-analysis-chart"
          chartName="Cash_Flow_Analysis"
          formats={['png']}
          exportPosition="top-right"
          className="lg:col-span-2"
        >
          <Card className="glass-card shadow-soft">
            <CardHeader>
              <TooltipLabel
                label={<CardTitle className="text-lg">Cash Flow Analysis</CardTitle>}
                tooltip={outputTooltips.dashboard.cash_flow_analysis}
                asChild
              />
              <CardDescription>Operating, investing, and financing cash flows</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 md:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="year" stroke="#64748b" />
                    <YAxis stroke="#64748b" />
                    <Tooltip formatter={(value) => formatCurrencyContext(value * 1000000)} />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="operatingCF" 
                      stackId="1"
                      stroke="#10b981" 
                      fill="#10b981"
                      name="Operating CF"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="investingCF" 
                      stackId="1"
                      stroke="#f59e0b" 
                      fill="#f59e0b"
                      name="Investing CF"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="financingCF" 
                      stackId="1"
                      stroke="#3b82f6" 
                      fill="#3b82f6"
                      name="Financing CF"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </ChartWithExport>
      </div>

      {/* Hero Image */}
      <Card className="glass-card shadow-soft">
        <CardContent className="p-0">
          <div className="relative rounded-xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxkYXNoYm9hcmQlMjBhbmFseXRpY3N8ZW58MHx8fHwxNzU4NTQzNTE0fDA&ixlib=rb-4.1.0&q=85"
              alt="Dashboard Analytics"
              className="w-full h-48 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-purple-900/80"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <h3 className="text-2xl font-bold mb-2">Professional Financial Analytics</h3>
                <p className="text-blue-100">Real-time business intelligence and scenario modeling</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;